/**
 * Demandas e SLA: distribuicao, pendencias e comentarios da operacao.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams, type PartyRef } from './comum';

import { api, requestPage } from '../client';

export interface SLA {
  due_at: string | null;
  days_left: number;
  overdue: boolean;
  has_target: boolean;
}

export type DemandPriority = 'baixa' | 'normal' | 'alta' | 'urgente';

export interface Demand {
  id: string;
  number: number;
  stage: string;
  priority: DemandPriority;
  subject: string;
  policyholder: PartyRef;
  broker: PartyRef | null;
  product: PartyRef;
  modality: PartyRef;
  owner_label: string;
  open_pendings: number;
  /** Mensagens da conversa (P-EST-1). Opcional para respostas antigas. */
  comments_count?: number;
  sla: SLA;
  created_at: string;
}

/** Indicadores da fila inteira com os filtros, em `meta.summary` (P-EST-1). */
export interface DemandSummary {
  overdue: number;
  due_soon: number;
  with_pendings: number;
}

/** Origem de um registro do Follow up (Source do OnPoint, P-EST-1). */
export type CommentSource =
  | 'cadastro'
  | 'credito'
  | 'nomeacao'
  | 'pep'
  | 'resseguro'
  | 'subscricao'
  | 'cotacao'
  | 'outra';

export interface AssignmentInput {
  owner_id?: string | null;
  priority?: DemandPriority;
  stage_due_at?: string | null;
}

export interface Assignment {
  priority: DemandPriority;
  stage_due_at: string | null;
  owner_id: string | null;
}

export type PendingArea =
  | 'credito'
  | 'subscricao'
  | 'juridico'
  | 'comercial'
  | 'documentos'
  | 'operacional'
  // Origens do Follow up do OnPoint que nao sao etapa (P-COT-3).
  | 'cadastro'
  | 'pep'
  | 'resseguro';

export interface Pending {
  id: string;
  operation_id: string;
  area: PendingArea;
  description: string;
  status: 'aberta' | 'resolvida';
  opened_label: string;
  resolved_label: string;
  resolution: string;
  resolved_at: string | null;
  created_at: string;
}

export interface OperationComment {
  id: string;
  operation_id: string;
  author_label: string;
  body: string;
  created_at: string;
  /** Esteira, origem e titulo do registro (P-EST-1). Opcionais para respostas antigas. */
  origin_pipe_id?: string | null;
  source?: CommentSource;
  title?: string;
  sent_to_broker_at?: string | null;
  /** Lado de quem escreveu (P-COT-3). Opcional para respostas antigas. */
  author_side?: 'seguradora' | 'corretor';
}

export interface CommentInput {
  body: string;
  origin_pipe_id?: string;
  title?: string;
}

export const demands = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Demand>(`/api/v1/demands${query(params)}`, signal),
  assign: (operationId: string, input: AssignmentInput) =>
    api.patch<Assignment>(`/api/v1/operations/${operationId}/assignment`, input),
  pendings: (operationId: string, signal?: AbortSignal) =>
    api.get<Pending[]>(`/api/v1/operations/${operationId}/pendings`, signal),
  openPending: (operationId: string, area: PendingArea, description: string) =>
    api.post<Pending>(`/api/v1/operations/${operationId}/pendings`, {
      area,
      description,
    }),
  resolvePending: (operationId: string, pendingId: string, resolution: string) =>
    api.post<Pending>(
      `/api/v1/operations/${operationId}/pendings/${pendingId}/resolution`,
      { resolution },
    ),
  comments: (operationId: string, signal?: AbortSignal) =>
    api.get<OperationComment[]>(`/api/v1/operations/${operationId}/comments`, signal),
  comment: (operationId: string, body: string | CommentInput) =>
    api.post<OperationComment>(
      `/api/v1/operations/${operationId}/comments`,
      typeof body === 'string' ? { body } : body,
    ),
};
